import React, { useState, useEffect } from 'react';

// Available component types (should match backend)
const COMPONENT_TYPES = [
  { value: 'commercial_flight', label: 'Commercial Flight' },
  { value: 'private_flight', label: 'Private Flight' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'event', label: 'Event' },
  { value: 'rental_car', label: 'Rental Car' },
  { value: 'chauffeur_service', label: 'Chauffeur Service' },
  { value: 'memo', label: 'Memo' }
];

const DEFAULT_PACKAGE = {
  name: '',
  internal_code: '',
  description: '',
  paradigm: 'ea_travel',
  status: 'active',
  display_order: 0,
  is_trial: false,
  trial_days: null,
  published: false,
  base_price: 0,
  currency: 'USD',
  billing_period_days: 30,
  hide_price: false,
  executive_config: {
    limit_type: 'included_plus_extra',  // 'fixed', 'included_plus_extra', or 'unlimited'
    max_count: 10,
    included_count: 1,
    cost_per_additional: 0,
    limit_exceeded_behavior: 'warning'
  },
  teams_enabled: false,
  available_components: COMPONENT_TYPES.map(c => c.value),
  flight_verification_enabled: false,
  flight_alerts_config: {
    enabled: false,
    period: 'month',
    limit: 100,
    per_executive: false,
    limit_exceeded_behavior: 'warning'
  },
  document_parsing_config: {
    period: 'month',
    limit: 100,
    per_executive: false,
    limit_exceeded_behavior: 'warning'
  },
  sso_enabled: false,
  saml_sso_enabled: false,
  oidc_sso_enabled: false,
  ea_config: {
    limit_type: 'fixed',
    max_count: 5,
    included_count: null,
    cost_per_additional: null,
    limit_exceeded_behavior: 'warning'
  },
  dedicated_emails_executives: false,
  dedicated_emails_eas: false,
  sticky_notes_enabled: true,
  todos_enabled: true,
  audit_logs_enabled: false,
  rbac_enabled: false,
  urbanride_quickbook_enabled: false,
  booking_enabled: false,
  booking_markup_config: {
    flight_booking_fee_type: 'none',
    flight_booking_fee_flat: 0,
    flight_booking_fee_pct: 0,
    flight_booking_fee_min: 0,
    flight_booking_fee_max: 0,
    ancillary_markup_type: 'none',
    ancillary_markup_value: 0,
    hotel_booking_fee_type: 'none',
    hotel_booking_fee_flat: 0,
    hotel_booking_fee_pct: 0,
    hotel_booking_fee_min: 0,
    hotel_booking_fee_max: 0,
    hotel_prepaid_markup_type: 'none',
    hotel_prepaid_markup_flat: 0,
    hotel_prepaid_markup_pct: 0,
    hotel_prepaid_markup_min: 0,
    hotel_prepaid_markup_max: 0,
    hotel_pay_at_hotel_fee_type: 'none',
    hotel_pay_at_hotel_fee_flat: 0,
    hotel_pay_at_hotel_fee_pct: 0,
    hotel_pay_at_hotel_fee_min: 0,
    hotel_pay_at_hotel_fee_max: 0,
    hotel_commission_model: 'retain',
    hotel_rate_preference: 'prepaid',
    flight_payment_method: 'duffel_payments',
    hotel_payment_method: 'balance',
    customer_payment_methods: ['credit_card'],
    booker_collection_method: 'credit_card',
    fee_billing_mode: 'at_booking',
    show_booking_fee_separately: false,
    booking_fee_label: 'Booking Fee',
    fee_currency: 'USD',
  },
  booking_fee_credit_config: {
    enabled: false,
    credit_type: 'none',
    per_executive_amount: 0,
    flat_amount: 0,
    max_monthly_credit: 0,
    rollover_enabled: false,
    max_rollover_months: 0,
    currency: 'USD',
  }
};

const PackageFormModal = ({ isOpen, onClose, onSave, existingPackage = null }) => {
  const [formData, setFormData] = useState(DEFAULT_PACKAGE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (existingPackage) {
      setFormData({
        ...DEFAULT_PACKAGE,
        ...existingPackage,
        executive_config: {
          ...DEFAULT_PACKAGE.executive_config,
          ...existingPackage.executive_config
        },
        flight_alerts_config: {
          ...DEFAULT_PACKAGE.flight_alerts_config,
          ...existingPackage.flight_alerts_config
        },
        document_parsing_config: {
          ...DEFAULT_PACKAGE.document_parsing_config,
          ...existingPackage.document_parsing_config
        },
        ea_config: {
          ...DEFAULT_PACKAGE.ea_config,
          ...existingPackage.ea_config
        },
        booking_markup_config: {
          ...DEFAULT_PACKAGE.booking_markup_config,
          ...existingPackage.booking_markup_config
        },
        booking_fee_credit_config: {
          ...DEFAULT_PACKAGE.booking_fee_credit_config,
          ...existingPackage.booking_fee_credit_config
        }
      });
    } else {
      setFormData(DEFAULT_PACKAGE);
    }
    setError('');
  }, [existingPackage, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save package');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const toggleComponent = (componentValue) => {
    setFormData(prev => {
      const current = prev.available_components || [];
      if (current.includes(componentValue)) {
        return { ...prev, available_components: current.filter(c => c !== componentValue) };
      } else {
        return { ...prev, available_components: [...current, componentValue] };
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] overflow-y-auto py-2 sm:py-8">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-2 sm:mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              {existingPackage ? 'Edit Package' : 'Create Package'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl p-1"
            >
              &times;
            </button>
          </div>

          {/* Tab Navigation - scrollable on mobile */}
          <div className="flex space-x-2 sm:space-x-4 mt-3 sm:mt-4 overflow-x-auto pb-1 -mx-1 px-1">
            {['basic', 'limits', 'features', 'components', 'booking'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-4 sm:px-6 py-4">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            {/* Basic Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Package Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:ring-purple-500 focus:border-purple-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Internal Code
                    </label>
                    <input
                      type="text"
                      value={formData.internal_code || ''}
                      onChange={(e) => updateField('internal_code', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:ring-purple-500 focus:border-purple-500"
                      placeholder="e.g., BASIC_2024"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => updateField('description', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:ring-purple-500 focus:border-purple-500"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Application Paradigm
                  </label>
                  <select
                    value={formData.paradigm || 'ea_travel'}
                    onChange={(e) => updateField('paradigm', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="ea_travel">EA Travel (Executive Assistant / Executive)</option>
                    <option value="speaker_bureau">Speaker Bureau (Meeting Planner / Speaker)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Controls the UI terminology for organizations assigned this package</p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => updateField('status', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Order
                    </label>
                    <input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => updateField('display_order', parseInt(e.target.value) || 0)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency
                    </label>
                    <select
                      value={formData.currency}
                      onChange={(e) => updateField('currency', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Base Price (per billing period)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.base_price}
                      onChange={(e) => updateField('base_price', parseFloat(e.target.value) || 0)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Period (days)
                    </label>
                    <input
                      type="number"
                      value={formData.billing_period_days}
                      onChange={(e) => updateField('billing_period_days', parseInt(e.target.value) || 30)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.hide_price}
                      onChange={(e) => updateField('hide_price', e.target.checked)}
                      className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Hide price (show "Contact Us")</span>
                  </label>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_trial}
                      onChange={(e) => updateField('is_trial', e.target.checked)}
                      className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Trial Package</span>
                  </label>
                  {formData.is_trial && (
                    <div className="flex items-center ml-6 sm:ml-0">
                      <input
                        type="number"
                        value={formData.trial_days || ''}
                        onChange={(e) => updateField('trial_days', parseInt(e.target.value) || null)}
                        placeholder="Days"
                        className="w-20 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                      />
                      <span className="ml-2 text-sm text-gray-500">trial days</span>
                    </div>
                  )}
                </div>

                {/* Publish to Landing Page */}
                <div className="mt-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={formData.published}
                      onChange={(e) => updateField('published', e.target.checked)}
                      className="h-4 w-4 mt-0.5 text-blue-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900">Publish to Landing Page</span>
                      <p className="text-xs text-gray-500 mt-1">
                        When enabled, this package will appear in the public pricing comparison on the landing page.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Limits Tab */}
            {activeTab === 'limits' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Executive Limits */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3">Executive Limits</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Limit Type
                      </label>
                      <select
                        value={formData.executive_config?.limit_type || 'included_plus_extra'}
                        onChange={(e) => updateNestedField('executive_config', 'limit_type', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                      >
                        <option value="fixed">Fixed Maximum</option>
                        <option value="included_plus_extra">Included + Extra Cost</option>
                        <option value="unlimited">Unlimited</option>
                      </select>
                    </div>
                    {formData.executive_config?.limit_type !== 'unlimited' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Limit Behavior
                        </label>
                        <select
                          value={formData.executive_config?.limit_exceeded_behavior || 'warning'}
                          onChange={(e) => updateNestedField('executive_config', 'limit_exceeded_behavior', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                        >
                          <option value="hard_stop">Hard Stop</option>
                          <option value="warning">Warning</option>
                          <option value="soft_limit">Soft Limit</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {formData.executive_config?.limit_type === 'fixed' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Maximum Executives
                      </label>
                      <input
                        type="number"
                        value={formData.executive_config?.max_count || 10}
                        onChange={(e) => updateNestedField('executive_config', 'max_count', parseInt(e.target.value) || 10)}
                        className="w-full sm:w-32 border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                      />
                    </div>
                  )}

                  {formData.executive_config?.limit_type === 'included_plus_extra' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Included Executives
                        </label>
                        <input
                          type="number"
                          value={formData.executive_config?.included_count || 1}
                          onChange={(e) => updateNestedField('executive_config', 'included_count', parseInt(e.target.value) || 1)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cost per Additional
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.executive_config?.cost_per_additional || 0}
                          onChange={(e) => updateNestedField('executive_config', 'cost_per_additional', parseFloat(e.target.value) || 0)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* EA Limits */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3">EA (Executive Assistant) Limits</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Limit Type
                      </label>
                      <select
                        value={formData.ea_config?.limit_type || 'fixed'}
                        onChange={(e) => updateNestedField('ea_config', 'limit_type', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                      >
                        <option value="fixed">Fixed Maximum</option>
                        <option value="included_plus_extra">Included + Extra Cost</option>
                        <option value="unlimited">Unlimited</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Limit Behavior
                      </label>
                      <select
                        value={formData.ea_config?.limit_exceeded_behavior || 'warning'}
                        onChange={(e) => updateNestedField('ea_config', 'limit_exceeded_behavior', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                      >
                        <option value="hard_stop">Hard Stop</option>
                        <option value="warning">Warning</option>
                        <option value="soft_limit">Soft Limit</option>
                      </select>
                    </div>
                  </div>

                  {formData.ea_config?.limit_type === 'fixed' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Maximum EAs
                      </label>
                      <input
                        type="number"
                        value={formData.ea_config?.max_count || 5}
                        onChange={(e) => updateNestedField('ea_config', 'max_count', parseInt(e.target.value) || 5)}
                        className="w-full sm:w-32 border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                      />
                    </div>
                  )}

                  {formData.ea_config?.limit_type === 'included_plus_extra' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Included EAs
                        </label>
                        <input
                          type="number"
                          value={formData.ea_config?.included_count || 2}
                          onChange={(e) => updateNestedField('ea_config', 'included_count', parseInt(e.target.value) || 2)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cost per Additional EA
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.ea_config?.cost_per_additional || 0}
                          onChange={(e) => updateNestedField('ea_config', 'cost_per_additional', parseFloat(e.target.value) || 0)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Document Parsing Limits */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3">Document Parsing Limits</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Period
                      </label>
                      <select
                        value={formData.document_parsing_config?.period || 'month'}
                        onChange={(e) => updateNestedField('document_parsing_config', 'period', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                      >
                        <option value="day">Per Day</option>
                        <option value="week">Per Week</option>
                        <option value="month">Per Month</option>
                        <option value="unlimited">Unlimited</option>
                      </select>
                    </div>
                    {formData.document_parsing_config?.period !== 'unlimited' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Limit
                        </label>
                        <input
                          type="number"
                          value={formData.document_parsing_config?.limit || 100}
                          onChange={(e) => updateNestedField('document_parsing_config', 'limit', parseInt(e.target.value) || 100)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                        />
                      </div>
                    )}
                  </div>
                  {formData.document_parsing_config?.period !== 'unlimited' && (
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.document_parsing_config?.per_executive || false}
                          onChange={(e) => updateNestedField('document_parsing_config', 'per_executive', e.target.checked)}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Limit is per executive</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Features Tab */}
            {activeTab === 'features' && (
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.teams_enabled}
                      onChange={(e) => updateField('teams_enabled', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">Team Functionality</span>
                      <span className="text-xs sm:text-sm text-gray-500">Allow EAs to work in teams</span>
                    </div>
                  </label>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.flight_verification_enabled}
                      onChange={(e) => updateField('flight_verification_enabled', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">Flight Verification</span>
                      <span className="text-xs sm:text-sm text-gray-500">Verify flight details</span>
                    </div>
                  </label>

                  {/* Flight Alerts with metered config */}
                  <div className="p-3 sm:p-4 bg-gray-50 rounded-lg sm:col-span-2">
                    <label className="flex items-center cursor-pointer hover:bg-gray-100 -m-2 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={formData.flight_alerts_config?.enabled}
                        onChange={(e) => updateNestedField('flight_alerts_config', 'enabled', e.target.checked)}
                        className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                      />
                      <div className="ml-3">
                        <span className="block text-sm sm:text-base font-medium text-gray-900">Flight Alerts</span>
                        <span className="text-xs sm:text-sm text-gray-500">Real-time flight status updates</span>
                      </div>
                    </label>
                    {formData.flight_alerts_config?.enabled && (
                      <div className="mt-4 pt-4 border-t border-gray-200 space-y-3 sm:space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Period
                            </label>
                            <select
                              value={formData.flight_alerts_config?.period || 'month'}
                              onChange={(e) => updateNestedField('flight_alerts_config', 'period', e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                            >
                              <option value="day">Per Day</option>
                              <option value="week">Per Week</option>
                              <option value="month">Per Month</option>
                              <option value="unlimited">Unlimited</option>
                            </select>
                          </div>
                          {formData.flight_alerts_config?.period !== 'unlimited' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Limit
                              </label>
                              <input
                                type="number"
                                value={formData.flight_alerts_config?.limit || 100}
                                onChange={(e) => updateNestedField('flight_alerts_config', 'limit', parseInt(e.target.value) || 100)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base"
                              />
                            </div>
                          )}
                        </div>
                        {formData.flight_alerts_config?.period !== 'unlimited' && (
                          <div>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.flight_alerts_config?.per_executive || false}
                                onChange={(e) => updateNestedField('flight_alerts_config', 'per_executive', e.target.checked)}
                                className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">Limit is per executive</span>
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.sso_enabled}
                      onChange={(e) => updateField('sso_enabled', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">SSO Login</span>
                      <span className="text-xs sm:text-sm text-gray-500">Domain-specific single sign-on (Google/Microsoft)</span>
                    </div>
                  </label>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.saml_sso_enabled}
                      onChange={(e) => updateField('saml_sso_enabled', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">SAML 2.0 SSO</span>
                      <span className="text-xs sm:text-sm text-gray-500">Enterprise SAML single sign-on (Okta, Azure AD, etc.)</span>
                    </div>
                  </label>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.oidc_sso_enabled}
                      onChange={(e) => updateField('oidc_sso_enabled', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">OIDC SSO</span>
                      <span className="text-xs sm:text-sm text-gray-500">OpenID Connect single sign-on (Okta, Auth0, etc.)</span>
                    </div>
                  </label>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.dedicated_emails_executives}
                      onChange={(e) => updateField('dedicated_emails_executives', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">Exec Emails</span>
                      <span className="text-xs sm:text-sm text-gray-500">Dedicated email for executives</span>
                    </div>
                  </label>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.dedicated_emails_eas}
                      onChange={(e) => updateField('dedicated_emails_eas', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">EA Emails</span>
                      <span className="text-xs sm:text-sm text-gray-500">Dedicated email for EAs</span>
                    </div>
                  </label>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.sticky_notes_enabled}
                      onChange={(e) => updateField('sticky_notes_enabled', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">Sticky Notes</span>
                      <span className="text-xs sm:text-sm text-gray-500">Add notes to trip timelines</span>
                    </div>
                  </label>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.todos_enabled}
                      onChange={(e) => updateField('todos_enabled', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">Todos</span>
                      <span className="text-xs sm:text-sm text-gray-500">Task management for trips</span>
                    </div>
                  </label>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.audit_logs_enabled}
                      onChange={(e) => updateField('audit_logs_enabled', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">Audit Logs</span>
                      <span className="text-xs sm:text-sm text-gray-500">Trip, user, and login audit trails</span>
                    </div>
                  </label>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.rbac_enabled}
                      onChange={(e) => updateField('rbac_enabled', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">RBAC</span>
                      <span className="text-xs sm:text-sm text-gray-500">Role-based access control for permissions</span>
                    </div>
                  </label>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.urbanride_quickbook_enabled}
                      onChange={(e) => updateField('urbanride_quickbook_enabled', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">Urbanride Quickbook</span>
                      <span className="text-xs sm:text-sm text-gray-500">One-click chauffeur booking from trip timeline</span>
                    </div>
                  </label>

                  <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.booking_enabled}
                      onChange={(e) => updateField('booking_enabled', e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded flex-shrink-0"
                    />
                    <div className="ml-3">
                      <span className="block text-sm sm:text-base font-medium text-gray-900">Flight & Hotel Booking</span>
                      <span className="text-xs sm:text-sm text-gray-500">Search and book flights and hotels via Duffel</span>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Components Tab */}
            {activeTab === 'components' && (
              <div className="space-y-3 sm:space-y-4">
                <p className="text-sm text-gray-600">
                  Select which trip component types are available in this package:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {COMPONENT_TYPES.map(component => (
                    <label
                      key={component.value}
                      className={`flex items-center p-2 sm:p-3 rounded-lg cursor-pointer border-2 ${
                        (formData.available_components || []).includes(component.value)
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={(formData.available_components || []).includes(component.value)}
                        onChange={() => toggleComponent(component.value)}
                        className="h-4 w-4 text-purple-600 border-gray-300 rounded flex-shrink-0"
                      />
                      <span className="ml-2 text-xs sm:text-sm font-medium text-gray-900 truncate">
                        {component.label}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="flex space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => updateField('available_components', COMPONENT_TYPES.map(c => c.value))}
                    className="text-sm text-purple-600 hover:text-purple-800"
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={() => updateField('available_components', [])}
                    className="text-sm text-purple-600 hover:text-purple-800"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
            )}

            {/* Booking & Markup Tab */}
            {activeTab === 'booking' && (
              <div className="space-y-4 sm:space-y-6">
                <p className="text-sm text-gray-600">
                  Configure booking fees, markup, and payment methods for flight and hotel bookings.
                </p>

                {/* Flight Booking Fee */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <h3 className="text-base font-medium text-gray-900 mb-3">Flight Booking Fee</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fee Type</label>
                      <select
                        value={formData.booking_markup_config?.flight_booking_fee_type || 'none'}
                        onChange={(e) => updateNestedField('booking_markup_config', 'flight_booking_fee_type', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="none">No Fee</option>
                        <option value="flat">Flat Amount</option>
                        <option value="percentage">Percentage</option>
                        <option value="both">Flat + Percentage</option>
                      </select>
                    </div>
                    {(formData.booking_markup_config?.flight_booking_fee_type === 'flat' || formData.booking_markup_config?.flight_booking_fee_type === 'both') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Flat Amount</label>
                        <input type="number" step="0.01"
                          value={formData.booking_markup_config?.flight_booking_fee_flat || 0}
                          onChange={(e) => updateNestedField('booking_markup_config', 'flight_booking_fee_flat', parseFloat(e.target.value) || 0)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                      </div>
                    )}
                    {(formData.booking_markup_config?.flight_booking_fee_type === 'percentage' || formData.booking_markup_config?.flight_booking_fee_type === 'both') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Percentage (%)</label>
                        <input type="number" step="0.1"
                          value={formData.booking_markup_config?.flight_booking_fee_pct || 0}
                          onChange={(e) => updateNestedField('booking_markup_config', 'flight_booking_fee_pct', parseFloat(e.target.value) || 0)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                      </div>
                    )}
                    {(formData.booking_markup_config?.flight_booking_fee_type === 'percentage' || formData.booking_markup_config?.flight_booking_fee_type === 'both') && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Min Fee</label>
                          <input type="number" step="0.01"
                            value={formData.booking_markup_config?.flight_booking_fee_min || 0}
                            onChange={(e) => updateNestedField('booking_markup_config', 'flight_booking_fee_min', parseFloat(e.target.value) || 0)}
                            placeholder="0 = no minimum"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Max Fee Cap</label>
                          <input type="number" step="0.01"
                            value={formData.booking_markup_config?.flight_booking_fee_max || 0}
                            onChange={(e) => updateNestedField('booking_markup_config', 'flight_booking_fee_max', parseFloat(e.target.value) || 0)}
                            placeholder="0 = no cap"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Ancillary Markup */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <h3 className="text-base font-medium text-gray-900 mb-3">Ancillary Markup (Bags, Seats, etc.)</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Markup Type</label>
                      <select
                        value={formData.booking_markup_config?.ancillary_markup_type || 'none'}
                        onChange={(e) => updateNestedField('booking_markup_config', 'ancillary_markup_type', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="none">No Markup</option>
                        <option value="percentage">Percentage</option>
                        <option value="flat">Flat Amount (per item)</option>
                      </select>
                    </div>
                    {formData.booking_markup_config?.ancillary_markup_type !== 'none' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {formData.booking_markup_config?.ancillary_markup_type === 'percentage' ? 'Percentage (%)' : 'Flat Amount'}
                        </label>
                        <input type="number" step="0.01"
                          value={formData.booking_markup_config?.ancillary_markup_value || 0}
                          onChange={(e) => updateNestedField('booking_markup_config', 'ancillary_markup_value', parseFloat(e.target.value) || 0)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Hotel Prepaid Rates — Markup */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border-l-4 border-blue-400">
                  <h3 className="text-base font-medium text-gray-900 mb-1">Prepaid Hotel Rates — Markup</h3>
                  <p className="text-xs text-gray-500 mb-3">For prepaid rates, Veyage charges the customer the hotel price plus any markup via Stripe. Payment to Duffel is made from the Veyage balance. Commission, when available from Duffel, is handled per the commission model below.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Markup Type</label>
                      <select
                        value={formData.booking_markup_config?.hotel_prepaid_markup_type || 'none'}
                        onChange={(e) => updateNestedField('booking_markup_config', 'hotel_prepaid_markup_type', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="none">No Markup</option>
                        <option value="flat">Flat Amount</option>
                        <option value="percentage">Percentage</option>
                        <option value="both">Flat + Percentage</option>
                      </select>
                    </div>
                    {(formData.booking_markup_config?.hotel_prepaid_markup_type === 'flat' || formData.booking_markup_config?.hotel_prepaid_markup_type === 'both') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Flat Amount</label>
                        <input type="number" step="0.01"
                          value={formData.booking_markup_config?.hotel_prepaid_markup_flat || 0}
                          onChange={(e) => updateNestedField('booking_markup_config', 'hotel_prepaid_markup_flat', parseFloat(e.target.value) || 0)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                      </div>
                    )}
                    {(formData.booking_markup_config?.hotel_prepaid_markup_type === 'percentage' || formData.booking_markup_config?.hotel_prepaid_markup_type === 'both') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Percentage (%)</label>
                        <input type="number" step="0.1"
                          value={formData.booking_markup_config?.hotel_prepaid_markup_pct || 0}
                          onChange={(e) => updateNestedField('booking_markup_config', 'hotel_prepaid_markup_pct', parseFloat(e.target.value) || 0)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                      </div>
                    )}
                    {(formData.booking_markup_config?.hotel_prepaid_markup_type === 'percentage' || formData.booking_markup_config?.hotel_prepaid_markup_type === 'both') && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Min Markup</label>
                          <input type="number" step="0.01"
                            value={formData.booking_markup_config?.hotel_prepaid_markup_min || 0}
                            onChange={(e) => updateNestedField('booking_markup_config', 'hotel_prepaid_markup_min', parseFloat(e.target.value) || 0)}
                            placeholder="0 = no minimum"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Max Markup Cap</label>
                          <input type="number" step="0.01"
                            value={formData.booking_markup_config?.hotel_prepaid_markup_max || 0}
                            onChange={(e) => updateNestedField('booking_markup_config', 'hotel_prepaid_markup_max', parseFloat(e.target.value) || 0)}
                            placeholder="0 = no cap"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Hotel Pay at Hotel Rates — Service Fee */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border-l-4 border-green-400">
                  <h3 className="text-base font-medium text-gray-900 mb-1">Pay at Hotel Rates — Service Fee</h3>
                  <p className="text-xs text-gray-500 mb-3">For pay-at-hotel rates, the customer's credit card guarantees the room via Duffel. Service fees are billed to the organization account. Commission, when available from Duffel, is handled per the commission model below.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fee Type</label>
                      <select
                        value={formData.booking_markup_config?.hotel_pay_at_hotel_fee_type || 'none'}
                        onChange={(e) => updateNestedField('booking_markup_config', 'hotel_pay_at_hotel_fee_type', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="none">No Fee</option>
                        <option value="flat">Flat Amount</option>
                        <option value="percentage">Percentage</option>
                        <option value="both">Flat + Percentage</option>
                      </select>
                    </div>
                    {(formData.booking_markup_config?.hotel_pay_at_hotel_fee_type === 'flat' || formData.booking_markup_config?.hotel_pay_at_hotel_fee_type === 'both') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Flat Amount</label>
                        <input type="number" step="0.01"
                          value={formData.booking_markup_config?.hotel_pay_at_hotel_fee_flat || 0}
                          onChange={(e) => updateNestedField('booking_markup_config', 'hotel_pay_at_hotel_fee_flat', parseFloat(e.target.value) || 0)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                      </div>
                    )}
                    {(formData.booking_markup_config?.hotel_pay_at_hotel_fee_type === 'percentage' || formData.booking_markup_config?.hotel_pay_at_hotel_fee_type === 'both') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Percentage (%)</label>
                        <input type="number" step="0.1"
                          value={formData.booking_markup_config?.hotel_pay_at_hotel_fee_pct || 0}
                          onChange={(e) => updateNestedField('booking_markup_config', 'hotel_pay_at_hotel_fee_pct', parseFloat(e.target.value) || 0)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                      </div>
                    )}
                    {(formData.booking_markup_config?.hotel_pay_at_hotel_fee_type === 'percentage' || formData.booking_markup_config?.hotel_pay_at_hotel_fee_type === 'both') && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Min Fee</label>
                          <input type="number" step="0.01"
                            value={formData.booking_markup_config?.hotel_pay_at_hotel_fee_min || 0}
                            onChange={(e) => updateNestedField('booking_markup_config', 'hotel_pay_at_hotel_fee_min', parseFloat(e.target.value) || 0)}
                            placeholder="0 = no minimum"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Max Fee Cap</label>
                          <input type="number" step="0.01"
                            value={formData.booking_markup_config?.hotel_pay_at_hotel_fee_max || 0}
                            onChange={(e) => updateNestedField('booking_markup_config', 'hotel_pay_at_hotel_fee_max', parseFloat(e.target.value) || 0)}
                            placeholder="0 = no cap"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Hotel Commission Model */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border-l-4 border-amber-400">
                  <h3 className="text-base font-medium text-gray-900 mb-1">Hotel Commission Model</h3>
                  <p className="text-xs text-gray-500 mb-3">When Duffel provides commission on a rate (prepaid or pay-at-hotel), choose whether to retain it as revenue or pass the savings to the customer as a reduced price.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Commission Model</label>
                      <select
                        value={formData.booking_markup_config?.hotel_commission_model || 'retain'}
                        onChange={(e) => updateNestedField('booking_markup_config', 'hotel_commission_model', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="retain">Retain commission (charge full price)</option>
                        <option value="pass_through">Pass through (reduce customer price)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <h3 className="text-base font-medium text-gray-900 mb-3">Payment Methods</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Flights</label>
                      <div className="w-full border border-gray-200 bg-gray-100 rounded-md px-3 py-2 text-sm text-gray-600">
                        Customer Card (Duffel Payments)
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Client pays the airline directly via credit card. Booking fees are added to the monthly invoice.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">Hotels — Prepaid</label>
                      <div className="w-full border border-blue-200 bg-blue-50 rounded-md px-3 py-2 text-sm text-gray-600">
                        Veyage Balance
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Veyage collects from client via Stripe, pays Duffel via balance. Markup is included in the customer charge.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-green-700 mb-1">Hotels — Pay at Hotel</label>
                      <div className="w-full border border-green-200 bg-green-50 rounded-md px-3 py-2 text-sm text-gray-600">
                        Customer Card (Duffel Payments)
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Customer's credit card guarantees the room via Duffel. Service fees are billed to the organization account.</p>
                    </div>
                  </div>
                </div>

                {/* Display Settings */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <h3 className="text-base font-medium text-gray-900 mb-3">Display Settings</h3>
                  <div className="space-y-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.booking_markup_config?.show_booking_fee_separately || false}
                        onChange={(e) => updateNestedField('booking_markup_config', 'show_booking_fee_separately', e.target.checked)}
                        className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show booking fee as a separate line item in the booking summary</span>
                    </label>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fee Label</label>
                      <input
                        type="text"
                        value={formData.booking_markup_config?.booking_fee_label || 'Booking Fee'}
                        onChange={(e) => updateNestedField('booking_markup_config', 'booking_fee_label', e.target.value)}
                        placeholder="Booking Fee"
                        className="w-full sm:w-64 border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fee Currency</label>
                      <select
                        value={formData.booking_markup_config?.fee_currency || 'USD'}
                        onChange={(e) => updateNestedField('booking_markup_config', 'fee_currency', e.target.value)}
                        className="w-full sm:w-64 border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                  </div>

                  {/* Booker Collection & Fee Billing Mode */}
                  <div className="border-t pt-3 mt-3">
                    <h4 className="text-sm font-medium text-gray-800 mb-2">Hotel Collection & Billing</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Customer Payment Methods (Hotels)</label>
                        <div className="space-y-2 mt-1">
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(formData.booking_markup_config?.customer_payment_methods || [formData.booking_markup_config?.booker_collection_method || 'credit_card']).includes('credit_card')}
                              onChange={(e) => {
                                const current = formData.booking_markup_config?.customer_payment_methods || [formData.booking_markup_config?.booker_collection_method || 'credit_card'];
                                let updated;
                                if (e.target.checked) {
                                  updated = [...new Set([...current, 'credit_card'])];
                                } else {
                                  updated = current.filter(m => m !== 'credit_card');
                                  if (updated.length === 0) updated = ['ach']; // Must have at least one
                                }
                                updateNestedField('booking_markup_config', 'customer_payment_methods', updated);
                                updateNestedField('booking_markup_config', 'booker_collection_method', updated[0]);
                              }}
                            />
                            Credit Card (Stripe)
                          </label>
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(formData.booking_markup_config?.customer_payment_methods || [formData.booking_markup_config?.booker_collection_method || 'credit_card']).includes('ach')}
                              onChange={(e) => {
                                const current = formData.booking_markup_config?.customer_payment_methods || [formData.booking_markup_config?.booker_collection_method || 'credit_card'];
                                let updated;
                                if (e.target.checked) {
                                  updated = [...new Set([...current, 'ach'])];
                                } else {
                                  updated = current.filter(m => m !== 'ach');
                                  if (updated.length === 0) updated = ['credit_card']; // Must have at least one
                                }
                                updateNestedField('booking_markup_config', 'customer_payment_methods', updated);
                                updateNestedField('booking_markup_config', 'booker_collection_method', updated[0]);
                              }}
                            />
                            ACH Bank Transfer (Stripe)
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Payment methods available to clients for hotel bookings. Flights always use credit card (paid to airline directly).</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Fee Billing Mode</label>
                        <select
                          value={formData.booking_markup_config?.fee_billing_mode || 'at_booking'}
                          onChange={(e) => updateNestedField('booking_markup_config', 'fee_billing_mode', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="at_booking">Charge at Booking</option>
                          <option value="monthly_invoice">Add to Monthly Invoice</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">When hotel charges are collected. Flight booking fees are always added to the monthly invoice.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Booking Fee Credits */}
                <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-medium text-gray-900">Booking Fee Credits</h3>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={formData.booking_fee_credit_config?.enabled || false}
                        onChange={(e) => updateNestedField('booking_fee_credit_config', 'enabled', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      Enable Credits
                    </label>
                  </div>
                  {formData.booking_fee_credit_config?.enabled && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Credit Type</label>
                          <select
                            value={formData.booking_fee_credit_config?.credit_type || 'none'}
                            onChange={(e) => updateNestedField('booking_fee_credit_config', 'credit_type', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          >
                            <option value="none">None</option>
                            <option value="per_executive">Per Executive</option>
                            <option value="flat">Flat Amount</option>
                          </select>
                        </div>
                        {formData.booking_fee_credit_config?.credit_type === 'per_executive' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">$ per Executive / Month</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.booking_fee_credit_config?.per_executive_amount || 0}
                              onChange={(e) => updateNestedField('booking_fee_credit_config', 'per_executive_amount', parseFloat(e.target.value) || 0)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            />
                          </div>
                        )}
                        {formData.booking_fee_credit_config?.credit_type === 'flat' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Flat Credit / Month ($)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.booking_fee_credit_config?.flat_amount || 0}
                              onChange={(e) => updateNestedField('booking_fee_credit_config', 'flat_amount', parseFloat(e.target.value) || 0)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            />
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Max Monthly Credit ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.booking_fee_credit_config?.max_monthly_credit || 0}
                            onChange={(e) => updateNestedField('booking_fee_credit_config', 'max_monthly_credit', parseFloat(e.target.value) || 0)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">0 = no cap</p>
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mt-6">
                            <input
                              type="checkbox"
                              checked={formData.booking_fee_credit_config?.rollover_enabled || false}
                              onChange={(e) => updateNestedField('booking_fee_credit_config', 'rollover_enabled', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            Rollover Unused Credits
                          </label>
                        </div>
                        {formData.booking_fee_credit_config?.rollover_enabled && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Rollover Months</label>
                            <input
                              type="number"
                              min="0"
                              value={formData.booking_fee_credit_config?.max_rollover_months || 0}
                              onChange={(e) => updateNestedField('booking_fee_credit_config', 'max_rollover_months', parseInt(e.target.value) || 0)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            />
                            <p className="text-xs text-gray-500 mt-1">0 = no limit</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Preview Calculator */}
                <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                  <h3 className="text-base font-medium text-gray-900 mb-3">Fee Preview Calculator</h3>
                  <p className="text-xs text-gray-500 mb-2">Enter a sample base price to see the calculated fee.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Base Price</label>
                      <input type="number" step="1" defaultValue={500} id="preview_base"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        onChange={(e) => {
                          const base = parseFloat(e.target.value) || 0;
                          const cfg = formData.booking_markup_config || {};
                          // Flight preview
                          const ft = cfg.flight_booking_fee_type || 'none';
                          let ff = 0;
                          if (ft === 'flat') ff = cfg.flight_booking_fee_flat || 0;
                          else if (ft === 'percentage') ff = base * ((cfg.flight_booking_fee_pct || 0) / 100);
                          else if (ft === 'both') ff = (cfg.flight_booking_fee_flat || 0) + base * ((cfg.flight_booking_fee_pct || 0) / 100);
                          if ((cfg.flight_booking_fee_min || 0) > 0) ff = Math.max(ff, cfg.flight_booking_fee_min);
                          if ((cfg.flight_booking_fee_max || 0) > 0) ff = Math.min(ff, cfg.flight_booking_fee_max);
                          const fEl = document.getElementById('preview_flight_fee');
                          const fTEl = document.getElementById('preview_flight_total');
                          if (fEl) fEl.textContent = ff.toFixed(2);
                          if (fTEl) fTEl.textContent = (base + ff).toFixed(2);
                          // Hotel Prepaid preview
                          const hpt = cfg.hotel_prepaid_markup_type || 'none';
                          let hpf = 0;
                          if (hpt === 'flat') hpf = cfg.hotel_prepaid_markup_flat || 0;
                          else if (hpt === 'percentage') hpf = base * ((cfg.hotel_prepaid_markup_pct || 0) / 100);
                          else if (hpt === 'both') hpf = (cfg.hotel_prepaid_markup_flat || 0) + base * ((cfg.hotel_prepaid_markup_pct || 0) / 100);
                          if ((cfg.hotel_prepaid_markup_min || 0) > 0) hpf = Math.max(hpf, cfg.hotel_prepaid_markup_min);
                          if ((cfg.hotel_prepaid_markup_max || 0) > 0) hpf = Math.min(hpf, cfg.hotel_prepaid_markup_max);
                          const hpEl = document.getElementById('preview_hotel_prepaid_fee');
                          const hpTEl = document.getElementById('preview_hotel_prepaid_total');
                          if (hpEl) hpEl.textContent = hpf.toFixed(2);
                          if (hpTEl) hpTEl.textContent = (base + hpf).toFixed(2);
                          // Hotel Pay at Hotel preview
                          const haht = cfg.hotel_pay_at_hotel_fee_type || 'none';
                          let hahf = 0;
                          if (haht === 'flat') hahf = cfg.hotel_pay_at_hotel_fee_flat || 0;
                          else if (haht === 'percentage') hahf = base * ((cfg.hotel_pay_at_hotel_fee_pct || 0) / 100);
                          else if (haht === 'both') hahf = (cfg.hotel_pay_at_hotel_fee_flat || 0) + base * ((cfg.hotel_pay_at_hotel_fee_pct || 0) / 100);
                          if ((cfg.hotel_pay_at_hotel_fee_min || 0) > 0) hahf = Math.max(hahf, cfg.hotel_pay_at_hotel_fee_min);
                          if ((cfg.hotel_pay_at_hotel_fee_max || 0) > 0) hahf = Math.min(hahf, cfg.hotel_pay_at_hotel_fee_max);
                          const hahEl = document.getElementById('preview_hotel_payathotel_fee');
                          const hahTEl = document.getElementById('preview_hotel_payathotel_total');
                          if (hahEl) hahEl.textContent = hahf.toFixed(2);
                          if (hahTEl) hahTEl.textContent = (base + hahf).toFixed(2);
                        }}
                      />
                    </div>
                    <div className="text-sm space-y-1">
                      <p className="font-medium text-gray-700">Flight</p>
                      <p>Fee: <span id="preview_flight_fee" className="font-semibold text-blue-700">0.00</span></p>
                      <p>Customer pays: <span id="preview_flight_total" className="font-semibold text-green-700">0.00</span></p>
                    </div>
                    <div className="text-sm space-y-1">
                      <p className="font-medium text-blue-700">Hotel (Prepaid)</p>
                      <p>Markup: <span id="preview_hotel_prepaid_fee" className="font-semibold text-blue-700">0.00</span></p>
                      <p>Customer pays: <span id="preview_hotel_prepaid_total" className="font-semibold text-green-700">0.00</span></p>
                    </div>
                    <div className="text-sm space-y-1">
                      <p className="font-medium text-green-700">Hotel (Pay at Hotel)</p>
                      <p>Service fee: <span id="preview_hotel_payathotel_fee" className="font-semibold text-blue-700">0.00</span></p>
                      <p>Billed to org: <span id="preview_hotel_payathotel_total" className="font-semibold text-green-700">0.00</span></p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm sm:text-base"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 text-sm sm:text-base font-medium"
              disabled={loading}
            >
              {loading ? 'Saving...' : (existingPackage ? 'Update Package' : 'Create Package')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PackageFormModal;
